/**
 * 予実集計の共通処理。事業部モジュールはここを使って5行契約を満たす。
 *
 * **実績・見込みの振り分けは plEntryOf()(plRecognition.ts)だけで行う。**(2026-10-22 発注者決定: 経費は承認時点で計上)
 *  - 読み込むのは PL_QUERY_STATUSES(承認前+承認済み以降)。差戻し・取消は読まない
 *  - 実績 = 計上日の月が来た「承認済み以降」/ 見込み = 承認前 + 計上日の月がまだ来ていない「承認済み以降」
 *  - 月は計上日の月(posting_month)。承認日・支払日は使わない
 *  - ここで状態を直接並べて判定しない
 */
import { collection, getDocs, query, where, type QueryDocumentSnapshot } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { PL_QUERY_STATUSES, buildSummaryLine, type Account, type Budget, type DateStr, type DepartmentId, type MonthStr, type SummaryLine, type Transaction, type Yen } from '@core/lib/types';
import { plEntryOf, type PlCategory } from './plRecognition';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

/** 事業部×月の PL の対象取引(承認前+承認済み以降)。実績・見込みは plEntryOf() で分ける */
export async function fetchPlTransactions(
  departmentId: DepartmentId,
  month: MonthStr,
): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'transactions'),
      where('department_id', '==', departmentId),
      where('status', 'in', PL_QUERY_STATUSES),
      where('posting_month', '==', month),
    ),
  );
  return snap.docs.map((d) => withId<Transaction>(d));
}

/** plEntryOf() の扱いと計上月で取引を絞る。today を渡すとその日を基準に判定する */
export function filterPlCategory(
  transactions: Transaction[],
  category: PlCategory,
  month: MonthStr,
  today?: DateStr,
): Transaction[] {
  return transactions.filter((t) => {
    const e = plEntryOf(t, today);
    return e.category === category && e.month === month;
  });
}

/** 事業部×月の実績の取引 */
export async function fetchBookedTransactions(
  departmentId: DepartmentId,
  month: MonthStr,
): Promise<Transaction[]> {
  return filterPlCategory(await fetchPlTransactions(departmentId, month), 'actual', month);
}

/** 事業部×月の見込みの取引。実績には含めず、別列・別枠で示す */
export async function fetchPendingTransactions(
  departmentId: DepartmentId,
  month: MonthStr,
): Promise<Transaction[]> {
  return filterPlCategory(await fetchPlTransactions(departmentId, month), 'forecast', month);
}

export async function fetchBudgets(
  departmentId: DepartmentId,
  month: MonthStr,
): Promise<Budget[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'budgets'),
      where('department_id', '==', departmentId),
      where('month', '==', month),
    ),
  );
  return snap.docs.map((d) => withId<Budget>(d));
}

/**
 * 科目の pl_line に従って取引を5行へ畳む(振り分けの正は accounts.pl_line)。
 * **資金移動(type='transfer')はPLに含めない。** 口座間の付け替えであり損益ではないため、
 * 科目の設定に関わらずここで除外する(decisions.md 2026-08-20)。
 */
export function summarizeTransactions(
  transactions: Transaction[],
  accounts: Account[],
): SummaryLine {
  const lineOf = new Map(accounts.map((a) => [a.id, a.pl_line]));
  let revenue: Yen = 0;
  let cogs: Yen = 0;
  let expense: Yen = 0;

  for (const t of transactions) {
    if (t.type === 'transfer') continue;
    switch (lineOf.get(t.account_id) ?? null) {
      case 'revenue':
        revenue += t.amount;
        break;
      case 'cogs':
        cogs += t.amount;
        break;
      case 'expense':
        expense += t.amount;
        break;
      default:
        break; // 資産・負債(CF管理用)はPLに含めない
    }
  }
  return buildSummaryLine({ revenue, cogs, expense });
}

export function summarizeBudgets(budgets: Budget[], accounts: Account[]): SummaryLine {
  const lineOf = new Map(accounts.map((a) => [a.id, a.pl_line]));
  let revenue: Yen = 0;
  let cogs: Yen = 0;
  let expense: Yen = 0;

  for (const b of budgets) {
    switch (lineOf.get(b.account_id) ?? null) {
      case 'revenue':
        revenue += b.amount;
        break;
      case 'cogs':
        cogs += b.amount;
        break;
      case 'expense':
        expense += b.amount;
        break;
      default:
        break;
    }
  }
  return buildSummaryLine({ revenue, cogs, expense });
}

/** 科目別の内訳(事業部ビューのドリルダウン手前の粒度) */
export type AccountBreakdown = {
  accountId: string;
  accountName: string;
  plLine: Account['pl_line'];
  actual: Yen;
  budget: Yen;
};

export function breakdownByAccount(
  transactions: Transaction[],
  budgets: Budget[],
  accounts: Account[],
): AccountBreakdown[] {
  const map = new Map<string, AccountBreakdown>();
  const ensure = (accountId: string) => {
    let row = map.get(accountId);
    if (!row) {
      const account = accounts.find((a) => a.id === accountId);
      row = {
        accountId,
        accountName: account?.name ?? accountId,
        plLine: account?.pl_line ?? null,
        actual: 0,
        budget: 0,
      };
      map.set(accountId, row);
    }
    return row;
  };

  for (const t of transactions) ensure(t.account_id).actual += t.amount;
  for (const b of budgets) ensure(b.account_id).budget += b.amount;

  const order: Record<string, number> = { revenue: 0, cogs: 1, expense: 2 };
  return [...map.values()].sort(
    (a, b) => (order[a.plLine ?? 'z'] ?? 9) - (order[b.plLine ?? 'z'] ?? 9),
  );
}
